import axios from 'axios'
import createAuthRefreshInterceptor from 'axios-auth-refresh'

import pinia from '@/stores'
import router from '@/router'
import { useAuthStore } from '@/stores/auth.store'
import { showErrorMessage } from '@/notification-utils'

// The auth store and router sit downstream of this module (they import API
// files that import this one). The import cycle is safe because everything
// here is a hoisted function declaration and the cyclic bindings are only
// read at request time, never while the modules are still evaluating.
function logout() {
  const authStore = useAuthStore(pinia)

  authStore.logout()

  router.push({
    name: 'LoginEmail'
  })
}

// interceptor to catch errors
function errorInterceptor(error) {
  if (axios.isCancel(error)) {
    throw error
  }

  if (!error.response) {
    const isTimeout = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT'

    if (isTimeout && error.config) {
      const seconds = Math.round((error.config.timeout || 0) / 1000)
      const method = error.config.method?.toUpperCase()
      const baseURL = (error.config.baseURL || '').replace(/\/$/, '')
      const url = error.config.url || ''
      const fullUrl = url.startsWith('http') ? url : `${baseURL}${url.startsWith('/') ? '' : '/'}${url}`
      const endpoint = [method, fullUrl].filter(Boolean).join(' ')

      showErrorMessage(`Request ${endpoint} timed out after ${seconds} seconds`)
    } else {
      showErrorMessage(error.message)
    }

    throw error
  }

  const isNotFound = error.response.status === 404

  if (error.response.data.title) {
    let errors = Object
      .values(error.response.data.errors || [])
      .flat()

    const errorMessage = `${error.response.data.title}${errors.length ? `${errors.join(', ')}` : ''}`

    // A 404 is rethrown for the caller to handle (e.g. an unset setting), so an expected
    // "not found" is not surfaced to the user as an error notification.
    if (!isNotFound) {
      showErrorMessage(errorMessage)
    }
  }

  if (error.response.status === 401) {
    logout()
  }

  throw error
}

function requestInterceptor(request) {
  const authStore = useAuthStore(pinia)

  if (authStore.isAuthenticated) {
    request.headers[
      'Authorization'
    ] = `Bearer ${authStore.accessToken}`
  }

  return request
}

// Interceptor for responses
function responseInterceptor(response) {
  return response
}

async function refreshAuthLogic(failedRequest) {
  const authStore = useAuthStore(pinia)

  try {
    await authStore.refreshAccessToken()
    failedRequest.response.headers[
      'Authorization'
    ] = `Bearer ${authStore.accessToken}`
  } catch (error) {
    console.error(error)

    logout()

    throw error
  }
}

function createHttpClient(baseURL) {
  const options = {
    baseURL: baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json'
    }
  }


  const httpClient = axios.create(options)

  if (import.meta.env.DEV) {
    createAuthRefreshInterceptor(httpClient, refreshAuthLogic)
  }

  httpClient.interceptors.request.use(requestInterceptor, errorInterceptor)
  httpClient.interceptors.response.use(responseInterceptor, errorInterceptor)
  return httpClient
}

export default createHttpClient
